import { Typography, SvgIcon, CircularProgress } from '@mui/material'
import React, { ChangeEvent, memo, useCallback, useMemo, useState } from 'react'
import tw, { css } from 'twin.macro'
import { LandingPageMajor, RiskMinor } from '@shopify/polaris-icons'
import { showToast } from 'src/utils/toast'

interface Props {
  acceptTypes: Array<string>
  isLoading?: boolean
  fileSizeLimit?: number
  onSelect: (file: File) => void
}

// TODO: make default file input from this component instead of CSV input
const FileUploadInputComponent = ({ acceptTypes, isLoading, fileSizeLimit, onSelect }: Props) => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const onInputImage = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0]

        setError(null)

        if (fileSizeLimit && file.size > fileSizeLimit) {
          e.target.value = ''

          showToast(`File size is bigger then ${fileSizeLimit / 1000000}MB`, {
            variant: 'error',
          })

          setError('Please upload required file.')

          return
        }

        if (!acceptTypes.includes(file.type)) {
          e.target.value = ''

          showToast('File type is not supported', {
            variant: 'error',
          })

          setError('Please upload required file.')

          return
        }

        setError(null)
        onSelect(file)

        e.target.value = ''
      }
    },
    [onSelect],
  )

  const handleDragEnter = useCallback(() => {
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const inputAcceptTypes = useMemo(() => acceptTypes.join(','), [acceptTypes])

  return (
    <>
      <label htmlFor="banner-file">
        <div
          css={[
            tw`relative border rounded-lg border-dashed px-4 py-8 flex flex-1 flex-col justify-center items-center transition duration-300 ease-linear`,
            styles.container(isDragOver, error),
          ]}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <SvgIcon css={tw`w-5 h-5`}>
                <LandingPageMajor css={tw`text-shades-1000`} />
              </SvgIcon>
              <Typography css={tw`my-2`} component="span">
                <Typography css={tw`underline text-primary-500`} component="span">
                  Tap to upload
                </Typography>{' '}
                or drag and drop
              </Typography>
              <Typography variant="body2" css={tw`text-shades-400`}>
                PNG, JPG or PDF (max. 10MB)
              </Typography>
              <input
                type="file"
                id="banner-file"
                name="file"
                accept={inputAcceptTypes}
                onChange={onInputImage}
                css={styles.input}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
              />
            </>
          )}
        </div>
      </label>
      <input
        type="file"
        id="banner-file"
        name="file"
        accept={inputAcceptTypes}
        onChange={onInputImage}
        hidden
      />
      {error && (
        <div css={tw`mt-2 flex items-center gap-1`}>
          <RiskMinor width={20} css={tw`fill-critical-500`} />
          <Typography variant="body2" css={tw`text-critical-500`}>
            {error}
          </Typography>
        </div>
      )}
    </>
  )
}

const styles = {
  container: (isDragOver?: boolean, error?: string | null) => [
    isDragOver
      ? css`
          background-color: var(--mui-shades-50);
          border-color: var(--mui-primary-500);
        `
      : css`
          background-color: var(--mui-shades-0);
        `,
    error &&
      css`
        border-color: var(--mui-critical-500);
        border-style: solid;
      `,
  ],
  input: [
    css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      z-index: 1;
      appearance: none;
      -webkit-appearance: none;
      opacity: 0;
    `,
  ],
}

export const FileUploadInput = memo(FileUploadInputComponent)
